import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, Calendar, Newspaper, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Logo } from "@/components/Logo";
import { LangToggle } from "@/components/LangToggle";
import { ASSETS, HUBS } from "@/lib/brand";

export default function MemberHome() {
  const { user, t } = useApp();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);

  useEffect(() => {
    api.get("/events").then(({ data }) => setEvents(data.events.slice(0, 2))).catch(() => {});
    api.get("/news").then(({ data }) => setNews(data.news.slice(0, 2))).catch(() => {});
  }, []);

  return (
    <div className="px-6 pt-6 fade-up">
      <header className="flex items-center justify-between mb-8">
        <Logo size="sm" />
        <LangToggle />
      </header>

      <div className="relative rounded-2xl overflow-hidden gold-border mb-8" style={{ minHeight: 160 }}>
        <img src={ASSETS.logoSkyline} alt="" className="absolute inset-0 w-full h-full object-cover opacity-45" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(10,9,8,0.9), rgba(10,9,8,0.4))" }} />
        <div className="relative p-6">
          <div className="eyebrow">{t("tagline")}</div>
          <h1 className="font-serif-cu text-2xl mt-2 text-[color:var(--cu-text)]">Welcome, {user.first_name}</h1>
          <div className="flex gap-3 mt-4 flex-wrap">
            {HUBS.map((h) => <span key={h} className="font-mono-cu text-[0.6rem] tracking-[0.2em] uppercase text-[color:var(--cu-muted)]">{h}</span>)}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-10">
        {[["card", CreditCard, t("my_card")], ["events", Calendar, t("events")], ["news", Newspaper, t("news")]].map(([to, Icon, label]) => (
          <button key={to} onClick={() => navigate(`/app/${to}`)} data-testid={`home-quick-${to}`} className="cu-card p-4 flex flex-col items-center gap-2 hover:-translate-y-0.5 transition-transform">
            <Icon size={20} className="text-[color:var(--cu-gold)]" />
            <span className="text-[0.62rem] uppercase tracking-[0.1em] font-mono-cu text-[color:var(--cu-muted)]">{label}</span>
          </button>
        ))}
      </div>

      <Section title={t("upcoming_events")} onMore={() => navigate("/app/events")}>
        {events.length === 0 ? <Empty text={t("no_events")} /> : events.map((e) => (
          <button key={e.id} onClick={() => navigate("/app/events")} className="cu-card w-full p-4 flex items-center justify-between text-left mb-3">
            <div>
              <div className="font-serif-cu text-[color:var(--cu-text)]">{e.title}</div>
              <div className="eyebrow mt-1">{e.city} · {e.datetime_str}</div>
            </div>
            <ChevronRight size={18} className="text-[color:var(--cu-muted)]" />
          </button>
        ))}
      </Section>

      <Section title={t("latest_news")} onMore={() => navigate("/app/news")}>
        {news.length === 0 ? <Empty text={t("no_news")} /> : news.map((n) => (
          <button key={n.id} onClick={() => navigate("/app/news")} className="cu-card w-full p-4 text-left mb-3">
            <div className="font-serif-cu text-[color:var(--cu-text)]">{n.title}</div>
            <div className="eyebrow mt-1">{n.city}</div>
          </button>
        ))}
      </Section>
    </div>
  );
}

function Section({ title, onMore, children }) {
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="eyebrow">{title}</h2>
        <button onClick={onMore} className="text-[color:var(--cu-gold)]"><ChevronRight size={18} /></button>
      </div>
      {children}
    </div>
  );
}
function Empty({ text }) { return <p className="text-sm text-[color:var(--cu-muted)]">{text}</p>; }
